import * as React from 'react';
import { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { Dispatch, bindActionCreators } from 'redux';
import { recoverPasswordRequestAction } from '../../store/me/actions';
import { RootState } from '../../store';
import { BounceLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import { tr } from '../../utils/translations/translator';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft } from 'lucide-react';
import NewTcLogo from '../../assets/NewTcLogo';

interface DispatchProps {
  recoverPasswordRequest: (values: any) => any;
}
interface StateProps {
  isRecoverPasswordLoading: any;
  isRecoverPasswordErroring: any;
  didRecoverPasswordSucceed: any;
  email: any;
  sent: boolean;
}

type RecoverPasswordProps = StateProps & RouteComponentProps & DispatchProps;
export const RecoverPassword: React.FC<
  RecoverPasswordProps & RouteComponentProps
> = (props) => {
  const { t } = useTranslation();
  const required = (value: string) => (value ? undefined : 'Requerido');
  const validEmail = (value: string) => {
    // Updated regex to accept + symbol and more TLDs
    if (
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
        value,
      )
    ) {
      return undefined;
    }
    return 'El correo electrónico no es valido';
  };
  const [email, setemail] = useState(null);
  // Prefilled by the signup "email already exists" CTA.
  const prefillEmail =
    new URLSearchParams(props.location.search).get('email') || '';
  const [resend, setresend] = useState(false);
  const composeValidators =
    (...validators: any[]) =>
    (value: any) =>
      validators.reduce(
        (error, validator) => error || validator(value),
        undefined,
      );

  const onSubmit = (value: any) => {
    props.recoverPasswordRequest(value);
    setemail(value.email);
  };

  return resend || props.didRecoverPasswordSucceed ? (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: 'linear-gradient(to bottom left, #6633ff, #121619)',
      }}
    >
      <div className="w-full max-w-md">
        <div className="bg-[#1a1f26] rounded-2xl shadow-2xl p-8 space-y-6 border border-white/10">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#6633ff] to-[#8855ff] rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {tr(t, 'recover-pwd_title')}
            </h2>
            <p className="text-gray-400">
              {tr(t, 'recover-pwd_email-sent') +
                ' ' +
                email +
                ', ' +
                tr(t, 'recover-pwd_email-sent2')}
            </p>
          </div>
          <Link to="/login" className="block">
            <button
              type="button"
              className="w-full py-3 px-4 bg-gradient-to-r from-[#6633ff] to-[#8855ff] text-white font-semibold rounded-xl hover:from-[#5522ee] hover:to-[#7744ff] transition-all shadow-lg hover:shadow-xl"
            >
              {tr(t, 'got-it')}
            </button>
          </Link>
          <button
            type="button"
            onClick={() => {
              setresend(true);
              props.recoverPasswordRequest(email);
            }}
            className="w-full text-[#6633ff] font-semibold hover:text-[#5522ee] transition-colors"
          >
            {tr(t, 'resend-email')}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: 'linear-gradient(to bottom left, #6633ff, #121619)',
      }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Volver al login</span>
        </Link>

        <div className="bg-[#1a1f26] rounded-2xl shadow-2xl p-8 space-y-6 border border-white/10">
          <div className="text-center space-y-4">
            <div className="mx-auto">
              <NewTcLogo />
            </div>
            <h2 className="text-3xl font-bold text-white">
              {tr(t, 'recover-pwd_title')}
            </h2>
            <p className="text-gray-400">{tr(t, 'recover-pwd_subtitle')}</p>
          </div>

          <Form
            onSubmit={onSubmit}
            initialValues={{ email: prefillEmail }}
            //@ts-ignore
            render={({ handleSubmit, invalid, values }) => {
              return (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Field
                    name="email"
                    component="input"
                    validate={composeValidators(required, validEmail)}
                  >
                    {({ input, meta }) => (
                      <div className="space-y-2">
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            {...input}
                            type="email"
                            placeholder={tr(t, 'email-address')}
                            className={`w-full pl-11 pr-4 py-3 bg-[#0f1318] border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6633ff] focus:border-transparent transition-all text-white placeholder-gray-500 ${
                              meta.error && meta.touched
                                ? 'border-red-500'
                                : 'border-white/10'
                            }`}
                          />
                        </div>
                        {meta.error && meta.touched && (
                          <p className="text-sm text-red-500 font-medium">
                            {meta.error}
                          </p>
                        )}
                      </div>
                    )}
                  </Field>

                  <button
                    type="submit"
                    disabled={invalid || props.isRecoverPasswordLoading}
                    className="w-full py-3 px-4 bg-gradient-to-r from-[#6633ff] to-[#8855ff] text-white font-semibold rounded-xl hover:from-[#5522ee] hover:to-[#7744ff] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed relative"
                  >
                    {props.isRecoverPasswordLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <BounceLoader color="#ffffff" size={20} />
                        <span>Enviando...</span>
                      </div>
                    ) : (
                      tr(t, 'send-email')
                    )}
                  </button>
                </form>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isRecoverPasswordLoading: state.me.isRecoverPasswordLoading,
  isRecoverPasswordErroring: state.me.isRecoverPasswordErroring,
  didRecoverPasswordSucceed: state.me.didRecoverPasswordSucceed,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  recoverPasswordRequest: bindActionCreators(
    recoverPasswordRequestAction,
    dispatch,
  ),
});
export const RecoverPasswordConnected = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RecoverPassword),
);
